import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';
import { formatNumber, formatCurrency } from '../../../../../shared/utils/numberFormatter';

const SourceLocationMatrix = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [touchPoint, setTouchPoint] = useState('last');
  const [matrixData, setMatrixData] = useState([]);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [maxUserCount, setMaxUserCount] = useState(1);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Format source name (same as SourceChart)
  const formatSourceName = (source) => {
    if (!source || source === 'Direct') {
      return 'Direct';
    }
    try {
      let cleaned = source;
      
      // Handle android-app format first: android-app://com.linkedin.android -> linkedin.android
      if (source.includes('android-app://')) {
        cleaned = source.replace(/^android-app:\/\/?\/?/i, '');
        const parts = cleaned.split('/')[0].split('.');
        if (parts.length >= 3) {
          // Take last two parts: com.linkedin.android -> linkedin.android
          cleaned = parts.slice(-2).join('.');
        } else {
          cleaned = cleaned.split('/')[0];
        }
      } else {
        // Remove protocol (http://, https://, etc.)
        cleaned = cleaned.replace(/^[a-z]+:\/\/?\/?/i, '');
        
        // Remove path and query parameters (everything after first /)
        cleaned = cleaned.split('/')[0];
        
        // Remove port if present (e.g., example.com:8080)
        cleaned = cleaned.split(':')[0];
        
        // Remove www. prefix if present
        cleaned = cleaned.replace(/^www\./i, '');
        
        // Handle subdomains: if more than 2 parts, remove first part if it's short (m, api, app, etc.)
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          const firstPart = parts[0];
          // If first part is very short (1-2 chars) or common prefixes, remove it
          if (firstPart.length <= 1 || (firstPart.length === 2 && ['m', 'api', 'app'].includes(firstPart.toLowerCase()))) {
            cleaned = parts.slice(1).join('.');
          }
        }
      }
      
      // Capitalize first letter
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    } catch (error) {
      return source;
    }
  };

  useEffect(() => {
    const fetchMatrixData = async () => {
      if (!teamId) return;

      setIsLoading(true);
      try {
        const websiteIds = appliedFilters?.websites?.length > 0 
          ? appliedFilters.websites 
          : 'all';

        const params = {
          team_id: teamId,
          websites: websiteIds === 'all' ? 'all' : JSON.stringify(websiteIds),
          touchPoint,
          ...(appliedFilters?.dateRange?.from && { from: appliedFilters.dateRange.from }),
          ...(appliedFilters?.dateRange?.to && { to: appliedFilters.dateRange.to })
        };

        const response = await axiosInstance.get('/overview/source-location-matrix', { params });

        if (response.data?.success) {
          setMatrixData(response.data.matrixData || []);
          setSources(response.data.sources || []);
          setCountries(response.data.countries || []);
          setMaxUserCount(response.data.maxUserCount || 1);
        } else {
          setMatrixData([]);
          setSources([]);
          setCountries([]);
          setMaxUserCount(1);
        }
      } catch (error) {
        console.error('Error fetching source-location matrix:', error);
        setMatrixData([]);
        setSources([]);
        setCountries([]);
        setMaxUserCount(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatrixData();
  }, [teamId, touchPoint, appliedFilters]);

  // Calculate color intensity based on user count (0-100% opacity of accentColor)
  const getCellColor = (userCount) => {
    if (!userCount || userCount === 0) {
      return 'transparent';
    }
    const intensity = Math.min(userCount / maxUserCount, 1);
    // Convert hex to rgba for opacity
    const r = parseInt(styles.accentColor.slice(1, 3), 16);
    const g = parseInt(styles.accentColor.slice(3, 5), 16);
    const b = parseInt(styles.accentColor.slice(5, 7), 16);
    const opacity = 0.2 + (intensity * 0.6); // Range from 0.2 to 0.8
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Touch Point Toggle Component (same style as SourceChart)
  const TouchPointToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('first')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'first' ? styles.accentColor : styles.primaryBg,
          color: value === 'first' ? '#ffffff' : styles.textPrimary
        }}
      >
        First Touch Point
      </button>
      <button
        onClick={() => onChange('last')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'last' ? styles.accentColor : styles.primaryBg,
          color: value === 'last' ? '#ffffff' : styles.textPrimary
        }}
      >
        Last Touch Point
      </button>
    </div>
  );

  const handleCellMouseEnter = (event, country, source) => {
    setHoveredCell(`${country}|${source}`);
    setHoverPosition({ x: event.clientX, y: event.clientY });
  };

  const handleCellMouseMove = (event) => {
    setHoverPosition({ x: event.clientX, y: event.clientY });
  };

  const loading = parentLoading || isLoading;

  return (
    <div 
      className="p-4 md:p-6" 
      style={{ 
        backgroundColor: styles.primaryBg, 
        border: `1px solid ${styles.borderColor}`, 
        width: '100%', 
        maxWidth: '100%', 
        overflow: 'hidden', 
        boxSizing: 'border-box',
        position: 'relative',
        minWidth: 0, // Allows flex shrinking
        flex: '1 1 auto' // Takes available space but can shrink
      }}
    >
      <div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4" 
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        <h3 
          className="text-sm font-medium flex-shrink-0" 
          style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
        >
          Sources & Geography Cross Matrix
        </h3>
        <div className="flex-shrink-0">
          <TouchPointToggle value={touchPoint} onChange={setTouchPoint} />
        </div>
      </div>

      {loading ? (
        <div 
          className="flex items-center justify-center" 
          style={{ 
            height: '400px', 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box' 
          }}
        >
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading matrix data...
          </div>
        </div>
      ) : matrixData.length === 0 || sources.length === 0 ? (
        <div 
          className="flex items-center justify-center" 
          style={{ 
            height: '400px', 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box' 
          }}
        >
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No matrix data available
          </div>
        </div>
      ) : (
        <div 
          className="relative" 
          style={{ 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box' 
          }}
        >
          <div 
            className="overflow-x-auto overflow-y-auto" 
            style={{ 
              maxHeight: '600px', 
              width: '100%', 
              maxWidth: '100%',
              boxSizing: 'border-box',
              minWidth: 0 // Prevents table from forcing container expansion
            }}
          >
            <table 
              className="border-collapse" 
              style={{ 
                fontFamily: 'Raleway, sans-serif', 
                width: 'auto', 
                minWidth: '100%',
                tableLayout: 'auto',
                border: `1px solid ${styles.borderColor}`
              }}
            >
              <thead style={{ position: 'sticky', top: 0, backgroundColor: styles.primaryBg, zIndex: 10 }}>
                <tr>
                  <th 
                    className="p-2 text-left text-xs font-semibold"
                    style={{ 
                      color: styles.textPrimary, 
                      border: `1px solid ${styles.borderColor}`,
                      backgroundColor: styles.primaryBg,
                      minWidth: '120px'
                    }}
                  >
                    Country
                  </th>
                  {sources.map((source) => (
                    <th
                      key={source}
                      className="p-2 text-center text-xs font-semibold"
                      style={{ 
                        color: styles.textPrimary, 
                        border: `1px solid ${styles.borderColor}`,
                        backgroundColor: styles.primaryBg,
                        minWidth: '100px',
                        writingMode: sources.length > 10 ? 'vertical-rl' : 'horizontal-tb',
                        textOrientation: 'mixed'
                      }}
                    >
                      {formatSourceName(source)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixData.map((row, rowIndex) => (
                  <tr key={row.country}>
                    <td 
                      className="p-2 text-xs font-medium"
                      style={{ 
                        color: styles.textPrimary, 
                        border: `1px solid ${styles.borderColor}`,
                        backgroundColor: styles.primaryBg,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5
                      }}
                    >
                      {row.country}
                    </td>
                    {sources.map((source) => {
                      const cellData = row[source];
                      const userCount = cellData?.userCount || 0;
                      const cellKey = `${row.country}|${source}`;
                      const isHovered = hoveredCell === cellKey;

                      return (
                        <td
                          key={source}
                          className="p-2 text-center text-xs cursor-pointer transition-all"
                          style={{
                            backgroundColor: getCellColor(userCount),
                            border: `1px solid ${styles.borderColor}`,
                            color: userCount > 0 ? styles.textPrimary : styles.textSecondary,
                            fontWeight: userCount > 0 ? '600' : '400',
                            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: isHovered ? `0 0 8px ${styles.accentColor}40` : 'none',
                            zIndex: isHovered ? 20 : 1
                          }}
                          onMouseEnter={(e) => handleCellMouseEnter(e, row.country, source)}
                          onMouseMove={handleCellMouseMove}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          {userCount > 0 ? userCount.toLocaleString() : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Custom Tooltip */}
          {hoveredCell && (() => {
            const [country, source] = hoveredCell.split('|');
            const row = matrixData.find(r => r.country === country);
            const cellData = row?.[source];
            
            if (!cellData) return null;

            return (
              <div
                className="fixed bg-white p-3 border shadow-lg pointer-events-none"
                style={{
                  borderRadius: '4px',
                  borderColor: styles.borderColor,
                  fontFamily: 'Raleway, sans-serif',
                  zIndex: 1000,
                  left: `${hoverPosition.x + 10}px`,
                  top: `${hoverPosition.y + 10}px`,
                  maxWidth: '250px'
                }}
              >
                <p className="font-semibold text-sm mb-2" style={{ color: styles.accentColor }}>
                  {formatSourceName(source)} × {country}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs mb-1">
                  Users: {cellData.userCount?.toLocaleString() || 0}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs mb-1">
                  Web3 Users: {cellData.web3Users?.toLocaleString() || 0}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs mb-1">
                  Wallets Connected: {cellData.walletsConnected?.toLocaleString() || 0}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs mb-1">
                  Wallets Transacted: {cellData.walletsTransacted?.toLocaleString() || 0}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs mb-1">
                  Value Transacted: {formatNumber(cellData.valueTransacted || 0)}
                </p>
                <p style={{ color: styles.textPrimary }} className="text-xs">
                  Net Worth: {formatCurrency(cellData.networth || 0, '$', 1)}
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default SourceLocationMatrix;

